import React, { useState } from "react";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faKey, faEye, faEyeSlash, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";

import UserPool from "../../utils/UserPool";
import axios from "axios";
import config from "../../config/config.json";
import "./ResetPassword.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [formSwitch, setFormSwitch] = useState(true);
  const [errorForm1, setErrorForm1] = useState("");
  const [errorForm2, setErrorForm2] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const errorMessages = {
    CodeMismatchException: "Verification code is incorrect.",
    InvalidParameterException:
      "Verification code is incorrect or the new password is not strong enough. Your password must be 8 characters long and contain at least one number, special character, uppercase letter, and lowercase letter.",
    LimitExceededException:
      "Too many attempts. Please try again after some time.",
    UserNotFoundException:
      "Your password can't be reset. Please contact support.",
    InvalidPasswordException:
      "New password is not strong enough. Your password must be 8 characters long and contain at least one number, special character, uppercase letter, and lowercase letter.",
    ExpiredCodeException:
      'Your verification code has expired. Please click the "forgot password" link again.',
  };

  const createCognitoUser = () =>
    new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: UserPool,
    });

  const handlePasswordConfirmation = (event) => {
    event.preventDefault();
    setErrorForm2("");

    if (newPassword !== confirmPassword) {
      setErrorForm2("New password and confirm password do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const cognitoUser = createCognitoUser();

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: handlePasswordConfirmationSuccess,
        onFailure: handlePasswordConfirmationFailure,
      });
    } catch (error) {
      setIsLoading(false);
      setErrorForm2("An error occurred. Please try again.");
    }
  };

  const handlePasswordConfirmationSuccess = () => {
    setIsLoading(false);
    navigate("/login");
  };

  const handlePasswordConfirmationFailure = (err) => {
    setIsLoading(false);
    if (errorMessages[err.code]) {
      setErrorForm2(errorMessages[err.code]);
    } else {
      setErrorForm2(err.message);
    }
  };

  const handleInitialPasswordReset = (event) => {
    event.preventDefault();
    setErrorForm1("");
    setIsLoading(true);

    var cognitoUser = createCognitoUser();

    cognitoUser.forgotPassword({
      onSuccess: handleInitialPasswordResetSuccess,
      onFailure: handleInitialPasswordResetFailure,
      inputVerificationCode: null,
    });
  };

  const handleInitialPasswordResetSuccess = () => {
    setIsLoading(false);
    setFormSwitch(false);
  };

  const handleInitialPasswordResetFailure = (err) => {
    setIsLoading(false);
    
    if (errorMessages[err.code]) {
      setErrorForm1(errorMessages[err.code]);
    } else if (err.message === "User is disabled.") {
      setErrorForm1(
        "User has been disabled. Please contact your administrator."
      );
    } else if (
      err.message === "User password cannot be reset in the current state."
    ) {
      const userResend = axios.create();
      var url = `${config.api.url}userResendPassword`;
      let bodyData = JSON.stringify({ email: email });

      userResend
        .post(url, bodyData, {
          headers: { "Content-Type": "application/json" },
        })
        .then((response) => {
          if (response.status === 200) {
            setErrorForm1(
              "Your password has been reset. Please check your email for instructions."
            );
          } else {
            setErrorForm1(
              "You will first need to log in with your temporary password."
            );
          }
          navigate("/");
        })
        .catch(() => {
          setErrorForm1("An error occurred. Please try again.");
        });
    } else {
      setErrorForm1(err.message || "An error occurred. Please try again.");
    }
  };

  return (
    <div className="resetpassword-container">
      {/* Internet Explorer Warning */}
      <div className="px-3 pt-4 mt-4 d-none" id="iesection">
        <h5>
          <strong>Sorry!</strong> Internet Explorer is no longer Supported. 
          Please use Chrome, Safari, Firefox or Edge browser.
        </h5>
      </div>
      
      {/* Background Image with Overlay */}
      <div className="resetpassword-background">
        <div className="resetpassword-overlay"></div>
      </div>

      {/* Content Wrapper */}
      <div className="resetpassword-content">
        {/* Left Panel - Branding Info */}
        <div className="resetpassword-info-panel">
          <div className="resetpassword-brand-section">
            <div className="resetpassword-logo-container">
              <img
                src={require("../../asset/image/logos/trial-main-box-logo.png")}
                alt="SMi Trial Logo"
                className="resetpassword-brand-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            <h1 className="resetpassword-main-title">
              SMi Trial<sup>&trade;</sup>
            </h1>
            <h2 className="resetpassword-subtitle">
              Protocol Compliance Management
            </h2>
            
            <div className="resetpassword-features">
              <div className="resetpassword-feature-badge">
                <span className="resetpassword-feature-icon">✓</span>
                <span>Real-time Protocol Tracking</span>
              </div>
              <div className="resetpassword-feature-badge">
                <span className="resetpassword-feature-icon">✓</span>
                <span>Comprehensive Reporting</span>
              </div>
              <div className="resetpassword-feature-badge">
                <span className="resetpassword-feature-icon">✓</span>
                <span>Regulatory Compliance</span>
              </div>
            </div>

            <div className="resetpassword-learn-more">
              <a 
                href="https://www.sciencemedia.com/trial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resetpassword-learn-more-btn"
              >
                Learn More About SMi Trial
              </a>
            </div>
          </div>

          <div className="resetpassword-footer">
            <p>&copy; {new Date().getFullYear()} Science Media. All Rights Reserved.</p>
            <div className="resetpassword-footer-links">
              <a href="https://sciencemedia.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <span>•</span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Terms
              </a>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="resetpassword-form-panel">
          <div className="resetpassword-form-card">
            {formSwitch ? (
              /* Step 1: Email Input Form */
              <>
                <div className="resetpassword-form-header">
                  <img
                    src={require(`../../asset/image/logos/${config.site.logo}`)}
                    alt="Logo"
                    className="resetpassword-site-logo"
                  />
                  <h2 className="resetpassword-form-title">Forgot Password?</h2>
                  <p className="resetpassword-form-subtitle">
                    Enter your email to receive a verification code
                  </p>
                </div>

                <form onSubmit={handleInitialPasswordReset} className="resetpassword-form">
                  {errorForm1 && (
                    <div className="resetpassword-alert">
                      <span className="resetpassword-alert-icon">⚠</span>
                      <p>{errorForm1}</p>
                    </div>
                  )}

                  <div className="resetpassword-form-group">
                    <label htmlFor="emailInput" className="resetpassword-label">
                      Email Address
                    </label>
                    <div className="resetpassword-input-wrapper">
                      <FontAwesomeIcon icon={faEnvelope} className="resetpassword-input-icon" />
                      <input
                        type="email"
                        id="emailInput"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="resetpassword-input"
                        placeholder="Enter your email"
                        pattern="^\S+@\S+\.\S+$"
                        required
                        autoFocus
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className={`resetpassword-submit-btn ${isLoading ? "resetpassword-loading" : ""}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="resetpassword-spinner"></span>
                        <span>Sending Code...</span>
                      </>
                    ) : (
                      "Send Verification Code"
                    )}
                  </button>

                  <div className="resetpassword-back-to-login">
                    <a href="/login" className="resetpassword-back-link">
                      ← Back to Sign In
                    </a>
                  </div>
                </form>
              </>
            ) : (
              /* Step 2: Verification Code & New Password Form */
              <>
                <div className="resetpassword-form-header">
                  <img
                    src={require(`../../asset/image/logos/${config.site.logo}`)}
                    alt="Logo"
                    className="resetpassword-site-logo"
                  />
                  <h2 className="resetpassword-form-title">Reset Password</h2>
                  <p className="resetpassword-form-subtitle">
                    Enter the code sent to your email
                  </p>
                </div>

                <form onSubmit={handlePasswordConfirmation} className="resetpassword-form">
                  {errorForm2 && (
                    <div className="resetpassword-alert">
                      <span className="resetpassword-alert-icon">⚠</span>
                      <p>{errorForm2}</p>
                    </div>
                  )}

                  <div className="resetpassword-form-group">
                    <label htmlFor="codeInput" className="resetpassword-label">
                      Verification Code
                    </label>
                    <div className="resetpassword-input-wrapper">
                      <FontAwesomeIcon icon={faKey} className="resetpassword-input-icon" />
                      <input
                        type="text"
                        id="codeInput"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="resetpassword-input"
                        placeholder="Enter verification code"
                        required
                        autoFocus
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="resetpassword-form-group">
                    <label htmlFor="newPasswordInput" className="resetpassword-label">
                      New Password
                    </label>
                    <div className="resetpassword-input-wrapper">
                      <FontAwesomeIcon icon={faLock} className="resetpassword-input-icon" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        id="newPasswordInput"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="resetpassword-input"
                        placeholder="Enter your new password"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="resetpassword-password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        tabIndex={-1}
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  <div className="resetpassword-form-group">
                    <label htmlFor="confirmPasswordInput" className="resetpassword-label">
                      Confirm New Password
                    </label>
                    <div className="resetpassword-input-wrapper">
                      <FontAwesomeIcon icon={faLock} className="resetpassword-input-icon" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPasswordInput"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="resetpassword-input"
                        placeholder="Confirm your new password"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="resetpassword-password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  <div className="resetpassword-password-requirements">
                    <p className="resetpassword-requirements-title">Password Requirements:</p>
                    <ul className="resetpassword-requirements-list">
                      <li>At least 8 characters long</li>
                      <li>Contains at least one uppercase letter</li>
                      <li>Contains at least one lowercase letter</li>
                      <li>Contains at least one number</li>
                      <li>Contains at least one special character</li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    className={`resetpassword-submit-btn ${isLoading ? "resetpassword-loading" : ""}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="resetpassword-spinner"></span>
                        <span>Resetting Password...</span>
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </form>
              </>
            )}

            <div className="resetpassword-help-section">
              <FontAwesomeIcon icon={faCircleQuestion} />
              <span>Need help? </span>
              <a href="mailto:help@smitrial.com" className="resetpassword-help-link">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
